import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, XCircle, User, Users, Clock, Info } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { toast } from 'sonner';

interface JoinRequest {
  id: string;
  player_id: string;
  player_type: 'supabase' | 'internal';
  requested_team_side: 'home' | 'away';
  request_status: 'pending' | 'approved' | 'denied';
  requested_at: string;
  player_name?: string;
}

interface JoinRequestApprovalProps {
  gameId: string;
  homeTeamId?: number;
  awayTeamId?: number;
  homeTeamName?: string;
  awayTeamName?: string;
  canEdit: boolean;
}

export const JoinRequestApproval = ({
  gameId,
  homeTeamId,
  awayTeamId,
  homeTeamName,
  awayTeamName,
  canEdit
}: JoinRequestApprovalProps) => {
  const [joinRequests, setJoinRequests] = useState<JoinRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState<string | null>(null);
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();

  const currentUser = supabaseUser || internalUser;

  const loadJoinRequests = async () => {
    if (!gameId) return;

    console.log('Loading join requests for game:', gameId);
    try {
      const { data: requests, error } = await supabase
        .from('join_requests')
        .select('*')
        .eq('game_id', gameId)
        .eq('request_status', 'pending')
        .order('requested_at', { ascending: true });

      console.log('Join requests query result:', { requests, error });

      if (error) {
        console.error('Error loading join requests:', error);
        toast.error('Failed to load join requests');
        return;
      }

      // Enrich with player names
      const enrichedRequests = await Promise.all(
        (requests || []).map(async (request) => {
          let playerName = 'Unknown Player';
          
          if (request.player_type === 'supabase') {
            const { data: profile } = await supabase
              .from('profiles')
              .select('first_name, last_name, username')
              .eq('id', request.player_id)
              .single();
            
            if (profile) {
              playerName = profile.first_name && profile.last_name 
                ? `${profile.first_name} ${profile.last_name}`
                : profile.username || 'Unknown Player';
            }
          } else {
            const { data: internalUser } = await supabase
              .from('internal_users')
              .select('username')
              .eq('id', request.player_id)
              .single();
            
            if (internalUser) {
              playerName = internalUser.username || 'Unknown Player';
            }
          }

          return {
            ...request,
            player_name: playerName,
            player_type: request.player_type as 'supabase' | 'internal',
            request_status: request.request_status as 'pending' | 'approved' | 'denied',
            requested_team_side: request.requested_team_side as 'home' | 'away'
          };
        })
      );

      setJoinRequests(enrichedRequests);
    } catch (error) {
      console.error('Error loading join requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (requestId: string, approved: boolean) => {
    if (!currentUser?.id) return;

    setApproving(requestId);
    try {
      const { data, error } = await supabase.rpc('approve_join_request', {
        p_request_id: requestId,
        p_approver_id: currentUser.id,
        p_approved: approved,
        p_approver_type: supabaseUser ? 'supabase' : 'internal'
      });

      if (error) {
        console.error('RPC Error processing join request:', error);
        toast.error('Failed to process request. Please try again.');
        return;
      }

      const result = data as { success: boolean; error?: string; message?: string };
      if (result && !result.success) {
        console.error('Join request processing failed:', result.error);
        toast.error(result.error || 'Failed to process request');
        return;
      }

      if (error) {
        console.error('Error processing join request:', error);
        toast.error('Failed to process request. Please try again.');
        return;
      }

      toast.success(approved ? 'Player approved and added to roster!' : 'Join request denied.');
      
      // Reload the requests
      await loadJoinRequests();
    } catch (error) {
      console.error('Error processing join request:', error);
      toast.error('Failed to process request. Please try again.');
    } finally {
      setApproving(null);
    }
  };

  useEffect(() => {
    loadJoinRequests();
  }, [gameId]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Join Requests
          </CardTitle>
          <CardDescription>Loading pending join requests...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (joinRequests.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Join Requests
          </CardTitle>
          <CardDescription>Manage player requests to join your team</CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              No pending join requests. Players can request to join using the game ID: <strong>{gameId.substring(0, 8).toUpperCase()}</strong>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Join Requests
          <Badge variant="secondary">{joinRequests.length}</Badge>
        </CardTitle>
        <CardDescription>Review and approve player requests to join your teams</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {joinRequests.map((request) => (
          <div key={request.id} className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <User className="h-8 w-8 text-muted-foreground" />
              <div>
                <div className="font-medium">{request.player_name}</div>
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <Clock className="h-3 w-3" />
                  {new Date(request.requested_at).toLocaleDateString()}
                </div>
              </div>
              <Badge variant="outline">
                {request.requested_team_side === 'home' 
                  ? homeTeamName || 'Home Team'
                  : awayTeamName || 'Away Team'
                }
              </Badge>
            </div>
            
            {canEdit && (
              <div className="flex gap-2">
                <Button
                  onClick={() => handleApproval(request.id, true)}
                  disabled={approving === request.id}
                  size="sm"
                  variant="default"
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Approve
                </Button>
                <Button
                  onClick={() => handleApproval(request.id, false)}
                  disabled={approving === request.id}
                  size="sm"
                  variant="destructive"
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Deny
                </Button>
              </div>
            )}
            
            {!canEdit && (
              <Badge variant="outline">Approval Required</Badge>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};